// The mini-app bootstrap flow (replaces the old postMessage handshake):
//
//   1. SDK loads inside the mini-app page.
//   2. It looks for sk_jwt + sk_exp in the URL fragment (set there by the
//      Sidekick `/authorize` redirect).
//        a. If found and not expired → store in sessionStorage, strip the
//           fragment with history.replaceState, proceed.
//        b. If not found → check sessionStorage. If a non-expired JWT is
//           there, use it.
//        c. Otherwise → redirect to Sidekick `/authorize?app=<appId>`.
//           Sidekick will check the cookie, mint a JWT, redirect back
//           with the JWT in the fragment.
//
// The fragment is the right channel because (a) browsers do not send
// fragments to the server, so the JWT never appears in the mini-app's
// access logs, and (b) it survives history.replaceState removal so we
// can clean it up immediately after reading.

const STORAGE_JWT = 'sk_jwt';
const STORAGE_EXP = 'sk_exp';

export interface PersistedToken {
  jwt: string;
  exp: number;     // unix seconds
}

export function extractJwtFromHash(hash: string): PersistedToken | null {
  if (!hash) return null;
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  const jwt = params.get('sk_jwt');
  const expRaw = params.get('sk_exp');
  if (!jwt || !expRaw) return null;
  const exp = Number(expRaw);
  if (!Number.isInteger(exp) || exp <= 0) return null;
  return { jwt, exp };
}

export function persistJwt(t: PersistedToken): void {
  sessionStorage.setItem(STORAGE_JWT, t.jwt);
  sessionStorage.setItem(STORAGE_EXP, String(t.exp));
}

export function loadPersistedJwt(): PersistedToken | null {
  const jwt = sessionStorage.getItem(STORAGE_JWT);
  const expRaw = sessionStorage.getItem(STORAGE_EXP);
  if (!jwt || !expRaw) return null;
  const exp = Number(expRaw);
  if (!Number.isInteger(exp) || exp <= Math.floor(Date.now() / 1000)) return null;
  return { jwt, exp };
}

export function computeAuthorizeUrl(sidekickOrigin: string, appId: string): string {
  return `${sidekickOrigin}/authorize?app=${encodeURIComponent(appId)}`;
}

export interface BootstrapResult {
  jwt: string;
  exp: number;
}

export interface BootstrapDeps {
  sidekickOrigin: string;
  appId: string;
  // Injected for testability; default to real browser APIs in production.
  getHash?: () => string;
  setHash?: (newHashWithoutLeadingHash: string) => void;
  redirect?: (url: string) => void;
}

/**
 * Returns a usable JWT or never returns (because it triggers a redirect).
 * Test code should mock `redirect` to throw a sentinel error so callers
 * can assert "redirected to X".
 */
export function bootstrap(deps: BootstrapDeps): BootstrapResult {
  const getHash = deps.getHash ?? (() => location.hash);
  const setHash = deps.setHash ??
    ((h) => history.replaceState(null, '', location.pathname + location.search + (h ? '#' + h : '')));
  const redirect = deps.redirect ?? ((url) => { location.assign(url); });

  // Path A: JWT in URL fragment from /authorize redirect.
  const fromHash = extractJwtFromHash(getHash());
  if (fromHash) {
    persistJwt(fromHash);
    // Strip sk_jwt + sk_exp from the fragment so the user can't accidentally
    // share the URL with the token in it. Keep any unrelated fragment keys.
    const params = new URLSearchParams(getHash().replace(/^#/, ''));
    params.delete('sk_jwt');
    params.delete('sk_exp');
    setHash(params.toString());
    return fromHash;
  }

  // Path B: cached JWT from a prior bootstrap in this tab.
  const cached = loadPersistedJwt();
  if (cached) return cached;

  // Path C: no token → bounce through /authorize.
  redirect(computeAuthorizeUrl(deps.sidekickOrigin, deps.appId));
  // Unreachable in real browsers — the page is navigating away.
  throw new Error('redirected to /authorize');
}
